import type { Dispute } from "@redotech/redo-dispute-db-schema/dispute-definition";

export const disputeQueueStates = [
  "merchant_action_required",
  "redo_handling",
  "waiting_on_provider",
  "done",
] as const;

export type DisputeQueueState = (typeof disputeQueueStates)[number];

const settledStatuses = new Set<Dispute["status"]>([
  "won",
  "lost",
  "accepted",
  "charge_refunded",
  "prevented",
]);

function isPast(date: Date | null | undefined): boolean {
  return date != null && date < new Date();
}

/**
 * Which queue a dispute sits in: who owns its next step, not what its status is.
 *
 * A first-match ladder, and the order carries meaning:
 *
 * 1. `done` — Shopify Protect covered it, or it has settled.
 * 2. `waiting_on_provider` — evidence has gone in, so the bank has it now.
 * 3. `merchant_action_required` — past the evidence deadline. This sits above
 *    the automation check deliberately: once the window has closed there is
 *    nothing left for Redo to submit, so an overdue dispute is the merchant's
 *    even on a store with auto-evidence switched on.
 * 4. `redo_handling` — still needs a response, and automation will generate it.
 * 5. Otherwise `merchant_action_required`.
 *
 * That last line is what makes this state easy to misread: it is a fallthrough
 * rather than a condition. A dispute is the merchant's when nothing else has
 * claimed it — not settled, not with the bank, not being auto-answered.
 *
 * `automateEvidenceEnabled` is a team setting, so the same row is
 * `redo_handling` for one store and `merchant_action_required` for another.
 *
 * Mirrored in SQL by `queueStateSql` in dispute-repo, which the list and the
 * counts both run. The two have to agree; change one and change the other.
 */
export function getDisputeQueueState(
  dispute: Pick<
    Dispute,
    | "aiEvidenceGeneratedAt"
    | "autoEvidenceDisabled"
    | "evidenceDueBy"
    | "evidenceSentOn"
    | "shopifyProtectStatus"
    | "status"
  >,
  automateEvidenceEnabled: boolean,
): DisputeQueueState {
  if (
    dispute.shopifyProtectStatus === "PROTECTED" ||
    settledStatuses.has(dispute.status)
  ) {
    return "done";
  }

  if (dispute.evidenceSentOn || dispute.status === "under_review") {
    return "waiting_on_provider";
  }

  if (isPast(dispute.evidenceDueBy)) {
    return "merchant_action_required";
  }

  if (
    dispute.status === "needs_response" &&
    automateEvidenceEnabled &&
    !dispute.autoEvidenceDisabled
  ) {
    return "redo_handling";
  }

  return "merchant_action_required";
}
